/**
 * Content-derived reconciliation helpers for persisted AI suggestions.
 *
 * The persisted store holds SEMANTIC ops (design Q1/Q3), never resolved
 * positions. To keep a persisted record's status honest through undo/redo
 * (design Q8) we ask a content question, not a position question: "is this
 * suggestion's content PRESENT in the current doc?" Because the check is
 * content-derived, an undo (content removed) and a redo (content re-inserted)
 * automatically drive the flip with no bespoke undo listener.
 *
 * Presence is evidence about the DOCUMENT, never on its own a clinician
 * decision: an absent accept is undone, but content that merely agrees with a
 * pending suggestion does not accept it. Its reader (the reconciler) owns that
 * distinction; this module only answers the question.
 *
 * Every semantic op type the chat pipeline can persist is reconciled. Each op's
 * presence question is scoped as tightly as the op itself is anchored — an
 * insert is looked for only in the window its content would occupy next to its
 * anchor, a field op only at its target node — so text that happens to repeat
 * elsewhere in the doc can never be mistaken for this suggestion's content.
 * Where the accept left a STAMP identity in the doc, that identity is consulted
 * before position at all, and scopes the positional fallback (see
 * `isSuggestionContentPresent`). `null` (indeterminate) is still returned
 * wherever the answer cannot be made reliable; the reconciler then leaves the
 * record's status untouched.
 */
import type { EditorOperation } from '@seen/ai-agents'
import type {
  PersistedEditorOperation,
  StagedSuggestion,
} from '@seen/core/models/compositionNote'
import { effectiveSuggestionHtml } from '@seen/core/models/suggestionStagingBasic'
import { referenceStringToId } from '@seen/core/references'
import { generateJSON, type JSONContent } from '@tiptap/core'
import type { Node as PMNode, Schema } from '@tiptap/pm/model'
import type { Editor } from '@tiptap/react'
import { hasSuggestionStamp } from '#src/components/rich-text-editor/aiSuggestionStamp.ts'
import {
  acceptIdBelongsTo,
  collectHtmlReferencedConditionIds,
} from '#src/components/rich-text-editor/conditionMentionSideEffects.ts'
import {
  findFieldNodeAfter,
  findTaskItemNode,
  findUniqueTextRange,
} from '#src/contexts/CollaborativeEditorRegistry.tsx'

/**
 * Narrow B1's open `PersistedEditorOperation` union (which carries a tolerant
 * `{ type: string }` escape arm for forward-compat) back to ai-agents'
 * `EditorOperation` for re-resolution.
 * @param op - The persisted op to narrow.
 * @returns The op typed as EditorOperation for the known op types, or null for
 *   an unrecognized/forward-compat op the pipeline can't re-resolve this
 *   version.
 */
export function narrowPersistedOp(
  op: PersistedEditorOperation
): EditorOperation | null {
  switch (op.type) {
    case 'replaceText':
    case 'replaceNode':
    case 'insertAfterText':
    case 'insertBeforeText':
    case 'appendToSection':
    case 'insertAtEnd':
    case 'replaceAll':
    case 'setChoiceById':
    case 'setTaskItemChecked':
      // The precise arms of both unions are structurally identical for these
      // types; B1 persists them verbatim, so the cast is sound.
      return op as EditorOperation
    default:
      return null
  }
}

/**
 * Is the suggestion's content PRESENT in the current doc? Each op type asks the
 * question at the place its own accept would have written to:
 * - `setChoiceById` / `replaceNode`: the target field currently displays the
 *   suggested value.
 * - `setTaskItemChecked`: the target taskItem currently carries the suggested
 *   checked state.
 * - `appendToSection`: the content is under the heading's section body.
 * - `insertAfterText` / `insertBeforeText`: the content occupies the window
 *   immediately after / before the anchor text.
 * - `insertAtEnd`: the content occupies the tail of the doc.
 * - `replaceAll`: the content is the doc.
 * - `replaceText`: the replaced-away text is gone AND the replacement is in the
 *   doc.
 * The question is always asked of the content an ACCEPT WOULD INSERT: pass the
 * record so an island-edited suggestion is compared against the clinician's edit
 * rather than the AI's superseded original. Without it an accepted-with-edits
 * record reads absent and demotes itself to pending, and the re-accept that
 * follows inserts its content a second time. The same content is used for a
 * PENDING record: presence answers one question about one string, and letting
 * promotion judge the op while demotion judges the edit would let a record be
 * promoted on evidence it is then judged absent by.
 *
 * The edit is NOT substituted for the two ops whose presence question is not a
 * content-HTML question — `setChoiceById` compares a bare option value against a
 * node attribute, and `setTaskItemChecked` carries no content at all.
 *
 * IDENTITY BEFORE POSITION. Every positional question above asks "is content
 * LIKE this suggestion's sitting where its accept would have written?", which two
 * live defects showed is neither sufficient nor necessary:
 * - A later accept at the same anchor inserted a paragraph BETWEEN the anchor and
 *   this suggestion's already-placed chip, pushing it clean out of the insert
 *   window; the content was applied, but the window could not see it.
 * - `isContentInRange`'s condition-mention question ("does every referenced
 *   condition id have a mention node in range?") is satisfied by ANOTHER
 *   record's chip, which auto-promoted a record nobody accepted.
 * So when the record's id is known, the accept's own STAMP is consulted first:
 * a node in the doc carrying this suggestion's `acceptId` is proof its accept is
 * still applied, wherever it has since been pushed to (`hasSuggestionStamp`).
 * With no stamp of its own the record falls through to the positional question —
 * required, not merely tolerated: an undo of a stamped accept removes the chip,
 * and only the positional answer (`false`) drives the demotion + FHIR unlink.
 * That fallback in turn counts only chips stamped to THIS suggestion, so neither
 * another record's accept nor a hand-typed mention is read as its evidence.
 * Plain-text content carries no identity to key on and is unchanged.
 * @param editor - The editor whose current doc is checked.
 * @param op - The persisted op whose content presence is being reconciled.
 * @param record - The staging record the op belongs to, read for its `id` (the
 *   stamp identity above) and `editedContent`. Omit only where no record exists;
 *   a record without an `id` is answered positionally, exactly as before.
 * @param siblings - Every staged record on the same resource, so a windowed
 *   insert's search window can span the content its siblings at the SAME anchor
 *   also inserted (see `siblingInsertSpan`). Omit to ask the question with a
 *   window sized to this suggestion's content alone.
 * @returns Whether the content is present, or `null` when presence is
 *   INDETERMINATE — the op type isn't recognized this version, the op's target
 *   (choice field, heading, anchor text, taskItem) no longer resolves in the
 *   doc, or the op's own shape makes the two states indistinguishable. None of
 *   those is the same as "content undone": the caller must leave those records'
 *   status alone.
 */
export function isSuggestionContentPresent(
  editor: Editor,
  op: PersistedEditorOperation,
  record?: Partial<Pick<StagedSuggestion, 'id' | 'editedContent'>>,
  siblings?: readonly StagedSuggestion[]
): boolean | null {
  /* Narrow through ai-agents' EditorOperation (a true discriminated union)
   * first: PersistedEditorOperation's tolerant escape arm overlaps the precise
   * arms, so `op.fieldId` reads as `unknown` off the raw union. The narrowed
   * op has the precise field types. */
  const narrowed = narrowPersistedOp(op)
  if (!narrowed) {
    return null
  }
  const suggestionId = record?.id
  if (
    suggestionId !== undefined &&
    hasSuggestionStamp(editor.state.doc, suggestionId)
  ) {
    return true
  }
  const docEnd = editor.state.doc.content.size
  const displaced = siblingInsertSpan(editor, narrowed, suggestionId, siblings)
  switch (narrowed.type) {
    case 'setChoiceById':
      return isChoiceSet(editor, narrowed.fieldId, narrowed.value)
    case 'setTaskItemChecked':
      return isTaskItemInState(
        editor,
        narrowed.sectionHeading ?? null,
        narrowed.labelText,
        narrowed.checked
      )
    case 'appendToSection':
      return isContentInSection(
        editor,
        narrowed.headingText,
        effectiveSuggestionHtml(record, narrowed.content),
        suggestionId
      )
    case 'insertAfterText':
      return isContentBesideAnchor(
        editor,
        narrowed.anchorText,
        effectiveSuggestionHtml(record, narrowed.content),
        'after',
        suggestionId,
        displaced
      )
    case 'insertBeforeText':
      return isContentBesideAnchor(
        editor,
        narrowed.anchorText,
        effectiveSuggestionHtml(record, narrowed.content),
        'before',
        suggestionId,
        displaced
      )
    case 'insertAtEnd': {
      const content = effectiveSuggestionHtml(record, narrowed.content)
      const window = insertWindowSize(editor, content) + displaced
      return isContentInRange(
        editor,
        Math.max(0, docEnd - window),
        docEnd,
        content,
        suggestionId
      )
    }
    case 'replaceAll':
      // The op's content IS the whole doc after accept, so the doc is the
      // tightest available scope — and a whole-document needle is far too long
      // to collide with unrelated text.
      return isContentInRange(
        editor,
        0,
        docEnd,
        effectiveSuggestionHtml(record, narrowed.content),
        suggestionId
      )
    case 'replaceText':
      return isTextReplacementApplied(
        editor,
        narrowed.findText,
        effectiveSuggestionHtml(record, narrowed.newContent),
        suggestionId,
        (narrowed as { fillOnly?: unknown }).fillOnly === true
      )
    case 'replaceNode':
      return isFieldReplacementApplied(
        editor,
        narrowed.anchorText,
        effectiveSuggestionHtml(record, narrowed.newContent),
        suggestionId
      )
    /* Unreachable: `narrowPersistedOp` above does not admit this type, and it
     * is never persisted — a revision is stored as the op it replaced with the
     * prose swapped. Listed because the narrowed union carries it. */
    case 'reviseProposal':
      return null
  }
}

function isChoiceSet(
  editor: Editor,
  fieldId: string,
  value: string
): boolean | null {
  let present: boolean | null = null
  editor.state.doc.descendants((node) => {
    if (present !== null) {
      return false
    }
    if (node.type.name === 'choice' && node.attrs.fieldId === fieldId) {
      present = node.attrs.display === value
      return false
    }
    return true
  })
  return present
}

function isTaskItemInState(
  editor: Editor,
  sectionHeading: string | null,
  labelText: string,
  checked: boolean
): boolean | null {
  // Missing OR ambiguous label: the op's target does not resolve, so presence is
  // indeterminate — exactly as a deleted choice field is.
  const target = findTaskItemNode(editor, sectionHeading, labelText)
  if ('error' in target) {
    return null
  }
  return target.checked === checked
}

/*
 * True when the heading's section BODY contains the suggested content.
 *
 * The section runs from just after the heading node to the next heading at the
 * same or higher level (mirrors `findSectionEnd`); the heading's own text is
 * excluded so a needle that happens to match the heading never reads as present.
 * Returns `null` when the heading itself is missing (renamed/deleted) — presence
 * is indeterminate, not false.
 */
function isContentInSection(
  editor: Editor,
  headingText: string,
  contentHtml: string,
  suggestionId: string | undefined
): boolean | null {
  const bounds = findSectionBounds(editor, headingText)
  if (!bounds) {
    return null
  }
  return isContentInRange(
    editor,
    bounds.start,
    bounds.end,
    contentHtml,
    suggestionId
  )
}

/*
 * True when the content the op would insert next to `anchorText` currently sits
 * on that side of it.
 *
 * The anchor is the op's own targeting primitive, so re-resolving it with
 * `findUniqueTextRange` asks presence at exactly the position the accept wrote
 * to. A missing or newly-ambiguous anchor is indeterminate (`null`), never
 * "absent" — the same rule a renamed heading follows.
 */
function isContentBesideAnchor(
  editor: Editor,
  anchorText: string,
  contentHtml: string,
  side: 'after' | 'before',
  suggestionId: string | undefined,
  displaced: number
): boolean | null {
  const anchor = findUniqueTextRange(editor, anchorText)
  if ('error' in anchor) {
    return null
  }
  const window = insertWindowSize(editor, contentHtml) + displaced
  const [from, to] =
    side === 'after'
      ? [anchor.to, anchor.to + window]
      : [anchor.from - window, anchor.from]
  return isContentInRange(editor, from, to, contentHtml, suggestionId)
}

/*
 * True when a `replaceText` op's replacement is what the doc currently holds.
 *
 * Read as two halves because the op has no durable anchor of its own — its
 * `findText` is consumed by the accept: the original text must be GONE and the
 * replacement must be in the doc. The first half is what actually drives the
 * undo flip (an undo puts `findText` straight back), and it is definitive; the
 * second half only distinguishes "replacement applied" from "the clinician
 * deleted the whole passage".
 *
 * Indeterminate when the replacement's own text contains `findText` (e.g.
 * "diabetes" → "type 2 diabetes"): then `findText` is in the doc whether or not
 * the op was applied, and neither half can separate the two states.
 */
function isTextReplacementApplied(
  editor: Editor,
  findText: string,
  newContentHtml: string,
  suggestionId: string | undefined,
  fillOnly: boolean
): boolean | null {
  const replacementText = contentText(editor, newContentHtml)
  const findNeedle = normalizeText(findText)
  if (!replacementText || !findNeedle || replacementText.includes(findNeedle)) {
    return null
  }
  const docEnd = editor.state.doc.content.size
  if (rangeText(editor, 0, docEnd).includes(findNeedle)) {
    return false
  }
  /* Accepting a fill-only replace keeps the label colon its replacement left
   * out (see `keepingLabelColon`), so colons don't count. */
  return isContentInRange(
    editor,
    0,
    docEnd,
    newContentHtml,
    suggestionId,
    fillOnly ? withoutColons : undefined
  )
}

function withoutColons(text: string): string {
  return normalizeText(text.replaceAll(':', ' '))
}

/*
 * True when a `replaceNode` op's replacement field is the field currently
 * sitting after its anchor.
 *
 * Resolved exactly as the op itself resolves (unique anchor → first interactive
 * field at or after it), then compared on the one attribute that carries a
 * field's user-visible value, `display` — the same attribute `setChoiceById`
 * reconciles on. A replacement field whose type declares no `display` attr has
 * no comparable rendered state, so presence stays indeterminate rather than
 * guessed from an attribute set the AI is not required to reproduce verbatim.
 */
function isFieldReplacementApplied(
  editor: Editor,
  anchorText: string,
  newContentHtml: string,
  suggestionId: string | undefined
): boolean | null {
  const anchor = findUniqueTextRange(editor, anchorText)
  if ('error' in anchor) {
    return null
  }
  const target = findFieldNodeAfter(editor, anchor.to)
  if ('error' in target) {
    return null
  }
  const replacement = firstFieldNode(editor, newContentHtml)
  if (!replacement) {
    return null
  }
  if (replacement.type.name !== target.typeName) {
    return false
  }
  // A condition-mention replacement is a NODE question (see isContentInRange),
  // asked over the target node itself.
  if (collectHtmlReferencedConditionIds(editor, newContentHtml).size > 0) {
    return isContentInRange(
      editor,
      target.from,
      target.to,
      newContentHtml,
      suggestionId
    )
  }
  const current = editor.state.doc.nodeAt(target.from)
  if (!current || !('display' in replacement.attrs)) {
    return null
  }
  return current.attrs.display === replacement.attrs.display
}

/* A `Condition/...` mention reference. Version-agnostic: the id itself is
 * extracted by `referenceStringToId`, which drops any `/_history/` segment. */
const CONDITION_REFERENCE = /^Condition\//

/*
 * True when the content is present inside [from, to). Asked in two halves, and
 * content that carries both must satisfy BOTH — a chip alone is not evidence
 * that the prose an op wraps around it is present:
 * - NODE half — condition-mention content (the op's HTML carries `Condition/`
 *   refs): every referenced condition id must have a matching mention node
 *   inside the range. Matched version-agnostically by id (mirroring orphan
 *   detection) so a versioned chip and an unversioned AI-insert reference
 *   reconcile the same way. ONLY a chip this suggestion's own accept stamped
 *   counts — not a sibling record's, and not an unstamped one a clinician typed
 *   by hand. The question is version-agnostic, so without that scope two records
 *   proposing the same condition at the same anchor both read present off
 *   whichever one was actually accepted; and an undo of a genuine accept would
 *   be refused its demotion whenever a hand-typed chip for the same condition
 *   sat in range, stranding the encounter link. Content that merely agrees with
 *   a suggestion is not that suggestion.
 * - TEXT half — the content's visible text appears in the range. BOTH sides are
 *   rendered by the same pair of functions (`contentText` / `rangeText`, which
 *   parse through the editor schema and then run ProseMirror's `textBetween`
 *   with the same `mentionLeafText`), so the needle is exactly the string the
 *   document holds once the content is in it. A regex tag-stripper on the needle
 *   side and ProseMirror's renderer on the document side disagreed on every
 *   mention chip, on punctuation touching a closing tag (`</a>.`), on HTML
 *   entities, and on any node the schema drops — and a needle that can never
 *   match reads present:false forever, which leaves an accepted record `pending`
 *   and lets a second Accept insert its content into the note twice.
 *
 * Empty extracted text reads "not present" so a blank insert never reads as
 * accepted — unless the content's whole visible payload IS the condition
 * mentions the node half just confirmed.
 *
 * Plain text carries no stamp to scope it by, so a pending record whose text a
 * clinician typed still reads present — it is the RECONCILER, not this
 * predicate, that refuses to promote on that evidence.
 */
function isContentInRange(
  editor: Editor,
  from: number,
  to: number,
  contentHtml: string,
  // The record the question is asked FOR, when known — used only to skip chips
  // another suggestion's accept stamped. Undefined asks the question exactly as
  // it was asked before identity existed.
  suggestionId: string | undefined,
  // Applied to both sides of the text comparison.
  fold: (text: string) => string = (text) => text
): boolean {
  const start = Math.max(0, Math.min(from, editor.state.doc.content.size))
  const end = Math.max(start, Math.min(to, editor.state.doc.content.size))

  // Node half: condition mentions are read from the NODES, ownership-scoped.
  const referencedConditionIds = collectHtmlReferencedConditionIds(
    editor,
    contentHtml
  )
  if (referencedConditionIds.size > 0) {
    const rangeConditionIds = new Set<string>()
    editor.state.doc.nodesBetween(start, end, (node) => {
      const reference = node.attrs?.reference
      if (
        typeof reference === 'string' &&
        CONDITION_REFERENCE.test(reference)
      ) {
        if (
          suggestionId !== undefined &&
          !acceptIdBelongsTo(node.attrs?.acceptId, suggestionId)
        ) {
          return
        }
        rangeConditionIds.add(referenceStringToId(reference))
      }
    })
    if (![...referencedConditionIds].every((id) => rangeConditionIds.has(id))) {
      return false
    }
  }

  // Text half: the same rendering on both sides.
  const needle = fold(contentText(editor, contentHtml))
  if (!needle) {
    return referencedConditionIds.size > 0
  }
  return fold(rangeText(editor, start, end)).includes(needle)
}

/*
 * Doc positions an insert's content occupies once placed, plus slack for the
 * block boundaries an insert at an inline position introduces (a paragraph split
 * costs positions the parsed fragment itself does not carry). Derived from the
 * content rather than guessed, so the search window stays as tight as the
 * content is long: text that repeats further away than the content is wide can
 * never read as this suggestion's.
 */
const INSERT_WINDOW_SLACK = 8

function insertWindowSize(editor: Editor, contentHtml: string): number {
  return parseContent(editor, contentHtml).content.size + INSERT_WINDOW_SLACK
}

/* The three ops whose presence question is asked inside a window sized to their
 * own content, and so the only ones a sibling insert can push out of scope. A
 * field op writes to a node rather than a window, `appendToSection` already
 * searches the whole section body, and `replaceAll` the whole doc. */
type WindowedInsertOp = Extract<
  EditorOperation,
  { type: 'insertAtEnd' | 'insertAfterText' | 'insertBeforeText' }
>

function isWindowedInsert(op: EditorOperation): op is WindowedInsertOp {
  return (
    op.type === 'insertAtEnd' ||
    op.type === 'insertAfterText' ||
    op.type === 'insertBeforeText'
  )
}

/*
 * Do two ops write to the SAME insertion point? An `insertAtEnd` always resolves
 * to `doc.content.size`, so every one of them collides; the two anchored inserts
 * collide when they name the same anchor text on the same side of it.
 */
function sharesInsertAnchor(
  op: EditorOperation,
  other: EditorOperation
): other is WindowedInsertOp {
  switch (op.type) {
    case 'insertAtEnd':
      return other.type === 'insertAtEnd'
    case 'insertAfterText':
    case 'insertBeforeText':
      return other.type === op.type && other.anchorText === op.anchorText
    default:
      return false
  }
}

/*
 * How far this suggestion's inserted content can have been pushed away from its
 * anchor by OTHER suggestions that write to the same insertion point.
 *
 * Two ops sharing an anchor resolve to the same position, so accepting both
 * stacks their content there in click order — and whichever lands second sits
 * BETWEEN the anchor and the first one's content, pushing it clean out of a
 * window sized to that content alone. It then reads absent while sitting
 * untouched in the note, and every reader of this predicate acts on that: the
 * reconciler's next present→absent flip demotes an accept nobody undid and
 * re-paints its card over the text it already wrote, and the accept pipeline's
 * resume reads the same absence as licence to insert the content a second time.
 *
 * So the window has to span everything that could share the anchor. Siblings are
 * counted whatever their status: a status is two round trips behind the document
 * it describes, and gating on it would reintroduce the same lag the reconciler's
 * own projections exist to work around. The looser window is still bounded by
 * what the AI proposed at this one point, so text repeating anywhere further off
 * than that can no more be mistaken for this suggestion's content than before.
 */
function siblingInsertSpan(
  editor: Editor,
  op: EditorOperation,
  suggestionId: string | undefined,
  siblings: readonly StagedSuggestion[] | undefined
): number {
  if (!siblings || !isWindowedInsert(op)) {
    return 0
  }
  let span = 0
  for (const sibling of siblings) {
    if (sibling.id === suggestionId) {
      continue
    }
    const other = narrowPersistedOp(sibling.op)
    if (!other || !sharesInsertAnchor(op, other)) {
      continue
    }
    span += parseContent(
      editor,
      effectiveSuggestionHtml(sibling, other.content)
    ).content.size
  }
  return span
}

// generateJSON rebuilds the editor's whole schema on every call, and the
// reconciler asks a presence question for every persisted record on every
// document transaction — i.e. per keystroke. An op's content is immutable, so
// the parse is cached per schema (parsed nodes are immutable and safe to share).
const parsedContentCache = new WeakMap<Schema, Map<string, PMNode>>()

function parseContent(editor: Editor, contentHtml: string): PMNode {
  let bySchema = parsedContentCache.get(editor.schema)
  if (!bySchema) {
    bySchema = new Map()
    parsedContentCache.set(editor.schema, bySchema)
  }
  const cached = bySchema.get(contentHtml)
  if (cached) {
    return cached
  }
  const json = generateJSON(
    contentHtml,
    editor.extensionManager.extensions
  ) as JSONContent
  const parsed = editor.schema.nodeFromJSON(json)
  bySchema.set(contentHtml, parsed)
  return parsed
}

/* The first interactive (non-text atom) node the content parses to — the field a
 * replaceNode replacement carries. */
function firstFieldNode(editor: Editor, contentHtml: string): PMNode | null {
  let found: PMNode | null = null
  parseContent(editor, contentHtml).descendants((node) => {
    if (found) {
      return false
    }
    if (!node.isText && node.type.isAtom) {
      found = node
      return false
    }
    return true
  })
  return found
}

/*
 * How an inline atom renders into the text the presence check compares on.
 *
 * A mention is an `atom: true` LEAF whose label lives in a `display` attribute,
 * so ProseMirror renders it as nothing and the bare `' '` this used to pass
 * rendered it as a space. Either way the label was invisible on the DOCUMENT
 * side while the needle side kept it, so no content carrying a chip could ever
 * match itself. Rendering the label puts the two sides in agreement.
 *
 * Condition mentions are the exception: the node half of `isContentInRange`
 * already owns them, scoped by accept identity and matched version-agnostically
 * by id. Their chip label is free to diverge from the label the op proposed, so
 * they contribute a separator only rather than a second, stricter test the node
 * half did not ask for.
 *
 * Passed as an argument at each call site, never declared as the mention spec's
 * `leafText`: an explicitly passed `leafText` wins over the spec, so this stays
 * local to this module's presence questions. On the spec it would change
 * `node.textContent` app-wide, where a mention occupies ONE document position
 * but would contribute many characters — breaking every consumer that maps a
 * text offset back to a document position (search highlighting, anchor
 * matching).
 */
function mentionLeafText(node: PMNode): string {
  if (node.type.name !== 'mention') {
    return ' '
  }
  const reference = node.attrs?.reference
  if (typeof reference === 'string' && CONDITION_REFERENCE.test(reference)) {
    return ' '
  }
  const display = node.attrs?.display
  return typeof display === 'string' && display !== '' ? display : ' '
}

function rangeText(editor: Editor, from: number, to: number): string {
  return normalizeText(
    editor.state.doc.textBetween(from, to, '\n', mentionLeafText)
  )
}

/* The visible text an op's content HTML contributes to the document once it is
 * in it: parsed through the editor's own schema (so entities, whitespace,
 * attribute noise, and node types the schema drops are resolved exactly as an
 * insert resolves them) and then rendered by the same `textBetween` +
 * `mentionLeafText` the document side goes through. */
function contentText(editor: Editor, contentHtml: string): string {
  const parsed = parseContent(editor, contentHtml)
  return normalizeText(
    parsed.textBetween(0, parsed.content.size, '\n', mentionLeafText)
  )
}

function normalizeText(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

/*
 * The [start, end) document positions of a heading's section body: from just
 * after the heading node to the next heading at the same or higher level (or the
 * doc end). Returns `null` when no heading matches `headingText`.
 */
function findSectionBounds(
  editor: Editor,
  headingText: string
): { start: number; end: number } | null {
  const headings: {
    pos: number
    nodeSize: number
    level: number
    text: string
  }[] = []
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'heading') {
      headings.push({
        pos,
        nodeSize: node.nodeSize,
        level: node.attrs.level ?? 1,
        text: node.textContent,
      })
    }
    return true
  })
  const target = headings.find((h) => h.text === headingText)
  if (!target) {
    return null
  }
  const next = headings.find(
    (h) => h.pos > target.pos && h.level <= target.level
  )
  return {
    start: target.pos + target.nodeSize,
    end: next?.pos ?? editor.state.doc.content.size,
  }
}
